<template>
    <!-- Barra de admin al pie de la ficha: estado de publicación y acciones.
         Solo la monta la página si el usuario es admin. -->
    <section class="hf-admin" :aria-label="$t('admin_tools.title')">
        <div class="hf-admin__head">
            <span class="hf-admin__title"><i class="fas fa-user-shield" aria-hidden="true" /> {{ $t('admin_tools.title') }}</span>
            <span :class="['hf-admin__badge', `hf-admin__badge--${estadoClave}`]">{{ $t(`admin_tools.status.${estadoClave}`) }}</span>
        </div>

        <div class="hf-admin__actions">
            <button v-if="!estado.aprobado" type="button" class="hf-admin__btn hf-admin__btn--success" :disabled="!!enCurso" @click="pedir('aprobar', 'approve')">
                <b-spinner v-if="enCurso === 'aprobar'" small /><i v-else class="far fa-thumbs-up" aria-hidden="true" />
                {{ $t('admin_tools.actions.approve') }}
            </button>
            <button v-if="estado.eliminado" type="button" class="hf-admin__btn hf-admin__btn--outline" :disabled="!!enCurso" @click="pedir('deseliminar', 'undelete')">
                <b-spinner v-if="enCurso === 'deseliminar'" small /><i v-else class="fas fa-undo" aria-hidden="true" />
                {{ $t('admin_tools.actions.undelete') }}
            </button>
            <button v-else type="button" class="hf-admin__btn hf-admin__btn--danger" :disabled="!!enCurso" @click="pedir('eliminar', 'delete')">
                <b-spinner v-if="enCurso === 'eliminar'" small /><i v-else class="far fa-trash-alt" aria-hidden="true" />
                {{ $t('admin_tools.actions.delete') }}
            </button>
            <router-link target="_blank" rel="noopener" v-if="!estado.eliminado" :to="{ name: 'editar-espacio', params: { idalojamiento: String(data.idalojamiento) } }" class="hf-admin__btn hf-admin__btn--outline">
                <i class="far fa-edit" aria-hidden="true" /> {{ $t('admin_tools.actions.edit_listing') }}
            </router-link>
            <router-link target="_blank" rel="noopener" :to="`/bloquear/${data.idalojamiento}`" class="hf-admin__btn hf-admin__btn--outline">
                <i class="far fa-calendar-alt" aria-hidden="true" /> {{ $t('admin_tools.actions.block') }}
            </router-link>
            <router-link target="_blank" rel="noopener" :to="linkBackoffice" class="hf-admin__btn hf-admin__btn--outline">
                <i class="fas fa-table" aria-hidden="true" /> {{ $t('admin_tools.actions.backoffice') }}
            </router-link>
            <button type="button" class="hf-admin__btn hf-admin__btn--outline" :disabled="!!enCurso" @click="descargarImagenes">
                <b-spinner v-if="enCurso === 'imagenes'" small /><i v-else class="fas fa-file-archive" aria-hidden="true" />
                {{ $t(enCurso === 'imagenes' ? 'admin_tools.actions.images_loading' : 'admin_tools.actions.images') }}
            </button>
        </div>

        <b-modal ref="confirmModal" :title="$t('admin_tools.confirm.title')" centered>
            <p class="mb-0">{{ pendiente ? $t(`admin_tools.confirm.${pendiente.aviso}`) : '' }}</p>
            <template #modal-footer="{ cancel }">
                <button type="button" class="hf-admin__btn hf-admin__btn--outline" @click="cancel()">{{ $t('admin_tools.confirm.cancel') }}</button>
                <button type="button" class="hf-admin__btn hf-admin__btn--primary" @click="confirmar">{{ $t('admin_tools.confirm.ok') }}</button>
            </template>
        </b-modal>
    </section>
</template>

<script>
import axios from 'axios';
import { saveAs } from 'file-saver';
import apiUrls from '@/new-constants/apiUrls';
import { aviso } from './Ficha/guardarFicha';

export default {
    name: 'AdminBar',
    props: ['data'],
    data() {
        return { enCurso: null, pendiente: null };
    },
    computed: {
        estado() {
            return { aprobado: Number(this.data.aprobado) === 1, eliminado: Number(this.data.eliminado) === 1 };
        },
        estadoClave() {
            if (this.estado.eliminado) return 'deleted';
            return this.estado.aprobado ? 'published' : 'pending';
        },
        // Abre el BackOffice en la pestaña donde está HOY el alojamiento y
        // filtrado por su ID. Se recalcula al aprobar / eliminar desde la ficha.
        linkBackoffice() {
            const seccion = { deleted: 'eliminados', pending: 'sin_aprobar', published: 'activos' }[this.estadoClave];
            return { path: '/admin/backoffice', query: { seccion, alojamiento: String(this.data.idalojamiento) } };
        },
    },
    methods: {
        pedir(accion, avisoKey) {
            this.pendiente = { accion, aviso: avisoKey };
            this.$refs.confirmModal.show();
        },
        confirmar() {
            const p = this.pendiente;
            this.$refs.confirmModal.hide();
            if (p) this.ejecutar(p.accion);
        },
        // El zip se arma en la API (hay fotos de proveedores sin CORS).
        async descargarImagenes() {
            if (this.enCurso) return;
            this.enCurso = 'imagenes';
            const id = this.data.idalojamiento;
            try {
                const { data } = await axios.get(apiUrls.backofficeAccommodationImages(id), { responseType: 'blob', timeout: 0 });
                saveAs(data, `alojamiento-${id}-imagenes.zip`);
            } catch (err) {
                console.error('[AdminBar] Error al descargar imágenes', err);
                const sinFotos = err.response && err.response.status === 404;
                aviso(this, this.$t(sinFotos ? 'admin_tools.toast.no_images' : 'admin_tools.toast.error'), 'danger');
            } finally {
                this.enCurso = null;
            }
        },
        async ejecutar(accion) {
            if (this.enCurso) return;
            this.enCurso = accion;
            try {
                const { data } = await axios.post(apiUrls.backofficeAccommodationStatus(this.data.idalojamiento), { accion });
                const e = data.estado;
                // La ficha lee el estado del store: así se actualiza toda la página.
                this.$store.dispatch('accommodation/setAccommodation', {
                    ...this.data, aprobado: e.aprobado, eliminado: e.eliminado, fecha_aprobado: e.fecha_aprobado,
                });
                aviso(this, this.$t(data.correo ? 'admin_tools.toast.mail_sent' : 'admin_tools.toast.done'));
            } catch (err) {
                console.error('[AdminBar] Error en la acción', accion, err);
                aviso(this, this.$t('admin_tools.toast.error'), 'danger');
            } finally {
                this.enCurso = null;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.hf-admin {
    margin: 28px 0 8px;
    padding: 16px 20px;
    background: #fff;
    border: 1px solid $grey-10;
    border-left: 4px solid $hf-sec-pine;
    border-radius: 10px;

    &__head { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
    &__title { font-weight: 700; color: $hf-sec-pine; }
    &__badge {
        padding: 3px 10px;
        border-radius: 999px;
        font-size: .78rem;
        font-weight: 700;
        &--published { background: #DCFCE7; color: #166534; }
        &--pending   { background: #FEF3C7; color: #92400E; }
        &--deleted   { background: #FEE2E2; color: #991B1B; }
    }
    &__actions { display: flex; flex-wrap: wrap; gap: 8px; }
    &__btn {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 8px 16px;
        border: 1.5px solid transparent;
        border-radius: 8px;
        font-size: .9rem;
        font-weight: 700;
        text-decoration: none !important;
        cursor: pointer;
        &:disabled { opacity: .55; cursor: not-allowed; }
        &:focus-visible { outline: 2px solid #2563EB; outline-offset: 2px; }
        &--primary { background: $hf-sec-pine; color: #fff; }
        &--success { background: #059669; color: #fff; &:hover:not(:disabled) { background: #047857; } }
        &--danger  { background: #DC2626; color: #fff; &:hover:not(:disabled) { background: #B91C1C; } }
        &--outline { background: #fff; color: $hf-sec-pine; border-color: $grey-10; &:hover { border-color: $hf-sec-pine; } }
    }
    @media (max-width: 640px) {
        &__btn { flex: 1 1 calc(50% - 8px); justify-content: center; }
    }
}
</style>
